// Only main-product renders this element; shared galleries keep their behavior.
if (!customElements.get("pdp-media-announcer")) {
  customElements.define(
    "pdp-media-announcer",
    class extends HTMLElement {
      connectedCallback() {
        this.section = this.closest(".main-product");
        this.carousel = this.section?.querySelector(".swiper-product");
        if (!this.carousel) return;

        this.imageAlts = new Map(
          Object.entries(JSON.parse(this.dataset.imageAlts || "{}"))
        );
        this.hasInteracted = false;
        this.lastSlide = null;
        this.lastDescription = "";
        this.onInteraction = () => {
          this.hasInteracted = true;
        };
        this.onMediaMove = () => this.syncActiveImage();
        this.onMediaChange = () => this.updateAnnouncement();
        this.onSliderDestroy = () => this.clearAnnouncement();

        for (const event of ["pointerdown", "keydown", "click"]) {
          this.section.addEventListener(event, this.onInteraction, true);
        }

        // Splide is mounted lazily and replaced when variant media is filtered.
        // Observe only this carousel's mount classes, not other page sliders.
        this.observer = new MutationObserver(() => this.bindSlider());
        this.observer.observe(this.carousel, {
          attributes: true,
          attributeFilter: ["class"],
        });
        this.bindSlider();
      }

      bindSlider() {
        const slider = this.carousel.splide;
        if (!slider?.Components.Slides || slider === this.slider) return;

        this.unbindSlider();
        this.slider = slider;

        // Avoid duplicate speech if the bundled Splide gains a Live component.
        if (slider.Components.Live) slider.options = { live: false };

        slider.on("move.pdpAnnouncement", this.onMediaMove);
        slider.on("moved.pdpAnnouncement", this.onMediaChange);
        slider.on("destroy.pdpAnnouncement", this.onSliderDestroy);
        slider.Components.Slides.get().forEach(({ slide, index }) => {
          this.syncImage(slide, index);
        });
        this.updateAnnouncement();
      }

      syncActiveImage() {
        const slide = this.slider.Components.Slides.getAt(this.slider.index)?.slide;
        return this.syncImage(slide, this.slider.index);
      }

      syncImage(slide, index) {
        const image = slide?.matches('[data-type="image"]')
          ? slide.querySelector("img")
          : null;
        if (!image) return { slide, description: "" };

        // Saved media.alt is only a fallback when no thumbnail is available.
        // Match the media ID class, since variant selection can reorder slides.
        const mediaKey = Array.from(slide.classList).find((key) => this.imageAlts.has(key));
        const media = this.imageAlts.get(mediaKey);
        const title = this.dataset.productTitle || "";
        const thumbnailRoot = this.slider.options.thumbs
          ? document.getElementById(this.slider.options.thumbs)
          : this.section.querySelector('[id^="x-product-thumbnail-"]');
        const thumbnail = thumbnailRoot && this.section.contains(thumbnailRoot)
          ? thumbnailRoot.getElementsByClassName("x-thumbnail")[index]
              ?.querySelector("img")
          : null;

        const savedAlt = (media ? media.alt : image.getAttribute("alt"))?.trim();
        const fallbackAlt = savedAlt || title;

        // Keep the thumbnail equal to its existing parent label first.
        // Never rewrite that label or strip its prefix, number, or spaces.
        if (thumbnail) {
          const thumbnailAlt = thumbnail.closest("button")?.getAttribute("aria-label")
            ?? thumbnail.getAttribute("alt")
            ?? fallbackAlt;
          if (thumbnail.getAttribute("alt") !== thumbnailAlt) {
            thumbnail.setAttribute("alt", thumbnailAlt);
          }
        }

        // The large image must copy the selected thumbnail's alt exactly.
        // A saved image alt must not override the thumbnail's full text.
        const description = thumbnail?.getAttribute("alt") ?? fallbackAlt;
        if (image.getAttribute("alt") !== description) {
          image.setAttribute("alt", description);
        }

        // The enclosing button's accessible name takes precedence over alt.
        const button = image.closest("button");
        const label = "Zoom " + description + (title ? " for " + title : "");
        if (button && button.getAttribute("aria-label") !== label) {
          button.setAttribute("aria-label", label);
        }

        return { slide, description };
      }

      updateAnnouncement() {
        const { slide, description } = this.syncActiveImage();

        if (slide === this.lastSlide && description === this.lastDescription) {
          return;
        }
        this.lastSlide = slide;
        this.lastDescription = description;
        this.clearAnnouncement();

        // Keep initial load silent and ignore the hidden mobile carousel when
        // the desktop uses stacked images. Video/model controls stay unchanged.
        if (
          !this.hasInteracted ||
          !description ||
          !this.carousel.getClientRects().length
        ) {
          return;
        }

        const message = `Image ${this.slider.index + 1} of ${this.slider.length}: ${description}`;
        this.announcementTimer = setTimeout(() => {
          if (this.isConnected && this.carousel.getClientRects().length) {
            this.textContent = message;
          }
        }, 150);
      }

      clearAnnouncement() {
        clearTimeout(this.announcementTimer);
        this.textContent = "";
      }

      unbindSlider() {
        // Namespaced removal preserves the theme's other slide-change handlers.
        this.slider?.off("move.pdpAnnouncement moved.pdpAnnouncement destroy.pdpAnnouncement");
        this.slider = null;
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.unbindSlider();
        this.clearAnnouncement();
        for (const event of ["pointerdown", "keydown", "click"]) {
          this.section?.removeEventListener(event, this.onInteraction, true);
        }
      }
    }
  );
}
